import React, { useState, useEffect } from 'react';
import { X, Upload } from 'lucide-react';

const EditSettingsModal = ({ isOpen, onClose, onSave, initialData = {}, sectionTitle }) => {
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState({});
  const [previews, setPreviews] = useState({});

  useEffect(() => {
    if (initialData && typeof initialData === 'object') {
      const formDataWithoutFiles = { ...initialData };
      delete formDataWithoutFiles.logo;
      delete formDataWithoutFiles.favicon;
      setFormData(formDataWithoutFiles);
      setPreviews({
        logo: initialData.logo || '',
        favicon: initialData.favicon || ''
      });
    } else {
      setFormData({});
      setPreviews({});
    }
    setFiles({});
  }, [initialData]);

  useEffect(() => {
    return () => {
      Object.values(previews).forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [previews]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (field, event) => {
    const file = event.target.files[0];
    if (file) {
      setFiles(prev => ({
        ...prev,
        [field]: file
      }));
      
      const previewUrl = URL.createObjectURL(file);
      setPreviews(prev => ({
        ...prev,
        [field]: previewUrl
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const data = new FormData();

    // Append non-file fields
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        data.append(key, value);
      }
    });

    // Append files if they exist
    if (files.logo) {
      data.append('logo', files.logo);
    }
    if (files.favicon) {
      data.append('favicon', files.favicon);
    }

    onSave(data);
  };

  const getInputType = (key, value) => {
    if (key === 'logo' || key === 'favicon') return 'file';
    if (key.includes('password')) return 'password';
    if (key.includes('email')) return 'email';
    if (key.includes('url')) return 'url';
    if (key.includes('phone')) return 'tel';
    if (typeof value === 'string' && value?.includes('<p>')) return 'textarea';
    return 'text';
  };

  const formatLabel = (key) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const renderInput = (key, value, inputType) => {
    if (inputType === 'file') {
      return (
        <div className="space-y-2">
          <div className="relative">
            <input
              type="file"
              onChange={(e) => handleFileChange(key, e)}
              accept={key === 'logo' ? 'image/*' : '.ico,.png'}
              className="hidden"
              id={`file-${key}`}
            />
            <label
              htmlFor={`file-${key}`}
              className="flex items-center gap-2 w-full bg-gray-700 text-gray-100 rounded-lg p-3 
                       border border-gray-600 cursor-pointer hover:bg-gray-600 transition-colors"
            >
              <Upload className="w-5 h-5 text-gray-400" />
              <span className="flex-1 truncate">
                {files[key]?.name || value || 'اختر ملف'}
              </span>
            </label>
          </div>
          {(previews[key] || value) && (
            <div className="mt-2 relative w-full h-24 bg-gray-700 rounded-lg overflow-hidden">
              <img
                src={previews[key] || value}
                alt={`${key} preview`}
                className="w-full h-full object-contain"
              />
            </div>
          )}
        </div>
      );
    }

    if (inputType === 'textarea') {
      return (
        <textarea
          value={value || ''}
          onChange={(e) => handleInputChange(key, e.target.value)}
          rows={4}
          className="w-full bg-gray-700 text-gray-100 rounded-lg p-3 
                   focus:ring-2 focus:ring-blue-500 focus:border-transparent
                   border border-gray-600"
        />
      );
    }

    return (
      <input
        type={inputType}
        value={value || ''}
        onChange={(e) => handleInputChange(key, e.target.value)}
        className="w-full bg-gray-700 text-gray-100 rounded-lg p-3 
                 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                 border border-gray-600"
      />
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-2xl shadow-lg relative" dir="rtl">
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-100">
              تعديل {sectionTitle}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {Object.entries(initialData).map(([key, value]) => {
              if (key === 'id' || key === '_id') return null;

              const inputType = getInputType(key, value);

              return (
                <div key={key} className="space-y-2">
                  <label className="block text-gray-400 text-sm">
                    {formatLabel(key)}
                  </label>
                  {renderInput(key, formData[key] || value, inputType)}
                </div>
              );
            })}
          </div>

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:bg-gray-700 rounded-lg transition-colors"
            >
              إلغاء
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-lg
                       hover:bg-blue-600 transition-colors"
            >
              حفظ التغييرات
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSettingsModal;